import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  IconButton,
  Button,
  Tooltip,
  Avatar,
  Collapse,
  Alert,
  LinearProgress,
  Fab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Receipt as ReceiptIcon,
  CreditCard as CreditCardIcon,
  AccountBalance as BankIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Error as ErrorIcon,
  PostAdd as PostIcon,
  Autorenew as AutoIcon,
  FilterList as FilterIcon,
  Refresh as RefreshIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import {
  fetchPaymentTransactions,
  unfetchPaymentTransactions,
  postSinglePayment,
  enableAutoPosting,
  disableAutoPosting,
  checkAutoPostingStatus,
} from '../slices/payments';
import ErrorBoundary from './ErrorBoundary';
import errorReporter from '../utils/errorReporting';
// import { formatDistanceToNow } from 'date-fns'; // Will add this dependency later

// Simple time ago formatter (temporary until date-fns is added)
const formatTimeAgo = (date) => {
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  
  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
};

const PaymentCard = ({ payment, onExpand, expanded, onPost, autoPostingEnabled }) => {
  const getStatusConfig = (status, posted) => {
    if (posted) {
      return {
        color: 'success',
        icon: <CheckCircleIcon />,
        label: 'Posted',
        description: 'Applied to account'
      };
    }
    
    switch (status?.toUpperCase()) {
      case 'COMPLETED':
        return {
          color: 'warning',
          icon: <ScheduleIcon />,
          label: 'Ready to Post',
          description: 'Awaiting posting'
        };
      case 'APPROVED':
        return {
          color: 'info',
          icon: <ScheduleIcon />,
          label: 'Processing',
          description: 'Transaction approved'
        };
      case 'PENDING':
        return {
          color: 'default',
          icon: <ScheduleIcon />,
          label: 'Pending',
          description: 'Awaiting completion'
        };
      default:
        return {
          color: 'error',
          icon: <ErrorIcon />,
          label: status || 'Unknown',
          description: 'Needs attention'
        };
    }
  };

  const getPaymentMethodIcon = (cardBrand) => {
    if (!cardBrand) return <BankIcon />;
    return <CreditCardIcon />;
  };

  const statusConfig = getStatusConfig(payment.status, payment.posted);
  // Fix amount calculation - use totalMoney.amount like the original component
  const amount = payment.totalMoney?.amount ? (payment.totalMoney.amount / 100).toFixed(2) : '0.00';
  const createdAt = payment.createdAt ? new Date(payment.createdAt) : new Date();
  
  // Fix customer name - construct from firstName and lastName
  const customerName = payment.firstName && payment.lastName 
    ? `${payment.firstName} ${payment.lastName}`
    : payment.firstName || payment.lastName || 'Unknown Customer';
  
  return (
    <Card 
      sx={{ 
        mb: 2,
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: 4
        }
      }}
    >
      <CardContent sx={{ pb: 2 }}>
        {/* Header Row */}
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
          <Box display="flex" alignItems="center" gap={2}>
            <Avatar sx={{ bgcolor: statusConfig.color === 'success' ? 'success.main' : 'primary.main' }}>
              {getPaymentMethodIcon(payment.cardDetails?.card?.cardBrand)}
            </Avatar>
            <Box>
              <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
                ${amount}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {customerName}
              </Typography>
            </Box>
          </Box>
          
          <Box display="flex" alignItems="center" gap={1}>
            <Chip
              icon={statusConfig.icon}
              label={statusConfig.label}
              color={statusConfig.color}
              size="small"
              sx={{ fontWeight: 500 }}
            />
            {payment.receiptUrl && (
              <Tooltip title="View Receipt">
                <IconButton
                  size="small"
                  onClick={() => window.open(payment.receiptUrl, '_blank')}
                >
                  <ReceiptIcon />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>

        {/* Payment Info Row */}
        <Grid container spacing={2} mb={2}>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2" color="text.secondary">
              Transaction ID
            </Typography>
            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
              {payment.id?.substring(0, 16)}...
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2" color="text.secondary">
              {formatTimeAgo(createdAt)}
            </Typography>
            <Typography variant="body2">
              Receipt #{payment.receiptNumber || 'N/A'}
            </Typography>
          </Grid>
        </Grid>

        {/* Action Row */}
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Button
            size="small"
            startIcon={expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            onClick={() => onExpand(payment.id)}
          >
            {expanded ? 'Less Details' : 'More Details'}
          </Button>
          
          {!payment.posted && payment.status === 'COMPLETED' && (
            <Box display="flex" gap={1}>
              {autoPostingEnabled && (
                <Chip
                  icon={<AutoIcon />}
                  label="Auto-posting enabled"
                  size="small"
                  color="info"
                  variant="outlined"
                />
              )}
              <Button
                variant="contained"
                size="small"
                startIcon={<PostIcon />}
                onClick={() => onPost(payment.id)}
                disabled={autoPostingEnabled}
                color="primary"
              >
                Post Payment
              </Button>
            </Box>
          )}
          
          {payment.posted && (
            <Chip
              icon={<CheckCircleIcon />}
              label="Posted Successfully"
              color="success"
              variant="filled"
            />
          )}
        </Box>

        {/* Expandable Details */}
        <Collapse in={expanded}>
          <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
            <Grid container spacing={2}>
              {payment.cardDetails && (
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" gutterBottom>
                    Card Details
                  </Typography>
                  <Typography variant="body2">
                    <strong>Brand:</strong> {payment.cardDetails.card?.cardBrand}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Last 4:</strong> **** {payment.cardDetails.card?.last4}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Expires:</strong> {payment.cardDetails.card?.expMonth}/{payment.cardDetails.card?.expYear}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Status:</strong> {payment.cardDetails.status}
                  </Typography>
                </Grid>
              )}
              
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" gutterBottom>
                  Transaction Timeline
                </Typography>
                <Typography variant="body2">
                  <strong>Created:</strong> {createdAt.toLocaleString()}
                </Typography>
                {payment.cardDetails?.cardPaymentTimeline?.capturedAt && (
                  <Typography variant="body2">
                    <strong>Captured:</strong> {new Date(payment.cardDetails.cardPaymentTimeline.capturedAt).toLocaleString()}
                  </Typography>
                )}
                {payment.processingFee?.[0] && (
                  <Typography variant="body2">
                    <strong>Processing Fee:</strong> ${(payment.processingFee[0].amountMoney.amount / 100).toFixed(2)}
                  </Typography>
                )}
              </Grid>
              
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>
                  Customer Information
                </Typography>
                <Typography variant="body2">
                  <strong>Customer ID:</strong> {payment.customerId}
                </Typography>
                <Typography variant="body2">
                  <strong>Currency:</strong> {payment.currency || 'USD'}
                </Typography>
              </Grid>
            </Grid>
          </Box>
        </Collapse>
      </CardContent>
    </Card>
  );
};

const PaymentFilters = ({ onFilterChange, autoPostingEnabled, onToggleAutoPosting }) => {
  const [filterOpen, setFilterOpen] = useState(false);
  const [filters, setFilters] = useState({
    status: 'all',
    posted: 'all',
    dateRange: 'all'
  });

  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    onFilterChange(newFilters);
  };

  return (
    <Box sx={{ mb: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5" component="h1" sx={{ fontWeight: 600 }}>
          Payment Transactions
        </Typography>
        
        <Box display="flex" gap={1}>
          <Tooltip title="Toggle Auto-Posting">
            <Button
              variant={autoPostingEnabled ? "contained" : "outlined"}
              startIcon={<AutoIcon />}
              onClick={onToggleAutoPosting}
              color={autoPostingEnabled ? "success" : "primary"}
            >
              Auto-Post
            </Button>
          </Tooltip>
          
          <Button
            variant="outlined"
            startIcon={<FilterIcon />}
            onClick={() => setFilterOpen(!filterOpen)}
          >
            Filters
          </Button>
          
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => window.location.reload()}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      <Collapse in={filterOpen}>
        <Card sx={{ p: 2, mb: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <Typography variant="subtitle2" gutterBottom>Status</Typography>
              {/* Filter controls would go here */}
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography variant="subtitle2" gutterBottom>Posted Status</Typography>
              {/* Filter controls would go here */}
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography variant="subtitle2" gutterBottom>Date Range</Typography>
              {/* Filter controls would go here */}
            </Grid>
          </Grid>
        </Card>
      </Collapse>
    </Box>
  );
};

const PaymentStatsSummary = ({ payments }) => {
  const stats = useMemo(() => {
    const paymentsArray = Object.values(payments || {});
    
    const totalTransactions = paymentsArray.length;
    const completedTransactions = paymentsArray.filter(p => p.status === 'COMPLETED').length;
    const postedTransactions = paymentsArray.filter(p => p.posted).length;
    const pendingPosting = completedTransactions - postedTransactions;
    
    const totalAmount = paymentsArray.reduce((sum, p) => sum + (p.totalMoney?.amount || 0), 0);
    const postedAmount = paymentsArray
      .filter(p => p.posted)
      .reduce((sum, p) => sum + (p.totalMoney?.amount || 0), 0);
    const pendingAmount = paymentsArray
      .filter(p => p.status === 'COMPLETED' && !p.posted)
      .reduce((sum, p) => sum + (p.totalMoney?.amount || 0), 0);
    
    return {
      totalTransactions,
      completedTransactions,
      postedTransactions,
      pendingPosting,
      totalAmount: (totalAmount / 100).toFixed(2),
      postedAmount: (postedAmount / 100).toFixed(2),
      pendingAmount: (pendingAmount / 100).toFixed(2),
    };
  }, [payments]);

  return (
    <Grid container spacing={3} sx={{ mb: 3 }}>
      <Grid item xs={12} sm={6} md={3}>
        <Card>
          <CardContent>
            <Typography variant="h4" color="primary" sx={{ fontWeight: 600 }}>
              {stats.totalTransactions}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Total Transactions
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              ${stats.totalAmount}
            </Typography>
          </CardContent>
        </Card>
      </Grid>
      
      <Grid item xs={12} sm={6} md={3}>
        <Card>
          <CardContent>
            <Typography variant="h4" color="warning.main" sx={{ fontWeight: 600 }}>
              {stats.pendingPosting}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Pending Posting
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              ${stats.pendingAmount}
            </Typography>
          </CardContent>
        </Card>
      </Grid>
      
      <Grid item xs={12} sm={6} md={3}>
        <Card>
          <CardContent>
            <Typography variant="h4" color="success.main" sx={{ fontWeight: 600 }}>
              {stats.postedTransactions}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Posted
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              ${stats.postedAmount}
            </Typography>
          </CardContent>
        </Card>
      </Grid>
      
      <Grid item xs={12} sm={6} md={3}>
        <Card>
          <CardContent>
            <Typography variant="h4" color="info.main" sx={{ fontWeight: 600 }}>
              {stats.completedTransactions}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Completed
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              Ready for posting
            </Typography>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

const PaymentsModern = () => {
  const dispatch = useDispatch();
  const { payments, loading, error, autoPostingEnabled } = useSelector((state) => state.payments);
  const [expandedCard, setExpandedCard] = useState(null);
  const [filters, setFilters] = useState({ status: 'all', posted: 'all', dateRange: 'all' });

  useEffect(() => {
    try {
      dispatch(fetchPaymentTransactions());
      // Check auto-posting status on load
      dispatch(checkAutoPostingStatus());
      return () => dispatch(unfetchPaymentTransactions());
    } catch (error) {
      errorReporter.reportUIError(error, 'PaymentsModern', 'initial_load');
    }
  }, [dispatch]);

  const filteredPayments = useMemo(() => {
    const paymentsArray = Object.entries(payments || {}).map(([key, payment]) => ({
      ...payment,
      id: key,
      // Ensure consistent data structure
      amount: payment.totalMoney?.amount || 0,
      currency: payment.totalMoney?.currency || 'USD',
      name: payment.firstName && payment.lastName 
        ? `${payment.firstName} ${payment.lastName}`
        : payment.firstName || payment.lastName || 'Unknown Customer'
    }));
    
    return paymentsArray
      .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
  }, [payments]);

  const handlePostPayment = useCallback(async (transactionId) => {
    try {
      await dispatch(postSinglePayment(transactionId));
    } catch (error) {
      errorReporter.reportPaymentError(error, 'manual_post', {
        transaction_id: transactionId
      });
    }
  }, [dispatch]);

  const handleToggleAutoPosting = useCallback(() => {
    if (autoPostingEnabled) {
      dispatch(disableAutoPosting());
    } else {
      dispatch(enableAutoPosting());
    }
  }, [autoPostingEnabled, dispatch]);

  if (loading && !Object.keys(payments || {}).length) {
    return (
      <Box sx={{ p: 3 }}>
        <LinearProgress sx={{ mb: 2 }} />
        <Typography>Loading payment transactions...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load payment transactions: {error}
        </Alert>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={() => dispatch(fetchPaymentTransactions())}
        >
          Retry
        </Button>
      </Box>
    );
  }

  return (
    <ErrorBoundary component="PaymentsModern">
      <Box sx={{ p: 3, minHeight: '100vh', bgcolor: 'grey.50' }}>
        <PaymentFilters
          onFilterChange={setFilters}
          autoPostingEnabled={autoPostingEnabled}
          onToggleAutoPosting={handleToggleAutoPosting}
        />
        
        <PaymentStatsSummary payments={payments} />
        
        {autoPostingEnabled && (
          <Alert severity="info" sx={{ mb: 3 }} icon={<AutoIcon />}>
            <strong>Auto-posting is enabled.</strong> Completed transactions will be posted automatically every 5 minutes.
            Manual posting is disabled while auto-posting is active.
          </Alert>
        )}
        
        <Box>
          {filteredPayments.length === 0 ? (
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 8 }}>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No payment transactions found
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Payment transactions will appear here when customers make payments.
                </Typography>
              </CardContent>
            </Card>
          ) : (
            filteredPayments.map((payment) => (
              <PaymentCard
                key={payment.id}
                payment={payment}
                expanded={expandedCard === payment.id}
                onExpand={setExpandedCard}
                onPost={handlePostPayment}
                autoPostingEnabled={autoPostingEnabled}
              />
            ))
          )}
        </Box>
        
        {loading && (
          <Box sx={{ position: 'fixed', bottom: 24, right: 24 }}>
            <Fab color="primary" disabled>
              <RefreshIcon />
            </Fab>
          </Box>
        )}
      </Box>
    </ErrorBoundary>
  );
};

export default PaymentsModern;